import { useEffect, useState, useCallback } from 'react';
import {
  Shield,
  AlertTriangle,
  Search,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Eye,
  Clock,
  Activity,
  Lock,
  Globe,
  Server,
  Wifi,
  WifiOff,
  ChevronDown,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  adminApi,
  AuditLogEntry,
  SecurityAlert,
  AuditLogStats,
  SecurityAlertSummary,
  AnomalySummary,
  LoggingConfig,
  Pagination,
} from '../services/api';
import clsx from 'clsx';

// ============================================================
// Sub-Components
// ============================================================

function SeverityBadge({ severity }: { severity: string }) {
  const styles: Record<string, string> = {
    INFO: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    LOW: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    WARNING: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    MEDIUM: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    HIGH: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    CRITICAL: 'bg-red-500/10 text-red-400 border-red-500/20',
  };

  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border', styles[severity] || styles.INFO)}>
      {severity}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    OPEN: 'bg-red-500/10 text-red-400 border-red-500/20',
    ACKNOWLEDGED: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    INVESTIGATING: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    RESOLVED: 'bg-green-500/10 text-green-400 border-green-500/20',
    FALSE_POSITIVE: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
  };

  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border', styles[status] || styles.OPEN)}>
      {status.replace('_', ' ')}
    </span>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  color = 'accent',
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
  color?: 'accent' | 'red' | 'yellow' | 'green' | 'blue';
}) {
  const colors = {
    accent: 'bg-accent/10 text-accent',
    red: 'bg-red-500/10 text-red-400',
    yellow: 'bg-yellow-500/10 text-yellow-400',
    green: 'bg-green-500/10 text-green-400',
    blue: 'bg-blue-500/10 text-blue-400',
  };

  return (
    <div className="card p-4">
      <div className="flex items-center gap-3">
        <div className={clsx('w-10 h-10 rounded-lg flex items-center justify-center', colors[color])}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-semibold text-foreground tabular-nums">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          <p className="text-xs text-foreground-tertiary">{label}</p>
        </div>
      </div>
    </div>
  );
}

function LoggingStatusCard({
  name,
  icon: Icon,
  enabled,
  details,
}: {
  name: string;
  icon: React.ElementType;
  enabled: boolean;
  details?: string;
}) {
  return (
    <div className="card p-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className={clsx(
          'w-8 h-8 rounded-lg flex items-center justify-center',
          enabled ? 'bg-green-500/10 text-green-400' : 'bg-zinc-500/10 text-zinc-500'
        )}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">{name}</p>
          {details && <p className="text-xs text-foreground-tertiary">{details}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {enabled ? (
          <span className="flex items-center gap-1 text-xs text-green-400">
            <Wifi className="w-3 h-3" /> Connected
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs text-zinc-500">
            <WifiOff className="w-3 h-3" /> Disabled
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Tabs
// ============================================================

type TabId = 'overview' | 'audit-logs' | 'security-alerts' | 'anomalies' | 'logging';

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'audit-logs', label: 'Audit Logs', icon: Search },
  { id: 'security-alerts', label: 'Security Alerts', icon: Shield },
  { id: 'anomalies', label: 'Anomaly Detection', icon: AlertTriangle },
  { id: 'logging', label: 'Centralized Logging', icon: Server },
];

// ============================================================
// Main Page
// ============================================================

export function AuditPage() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(24);

  // Overview state
  const [auditStats, setAuditStats] = useState<AuditLogStats | null>(null);
  const [alertSummary, setAlertSummary] = useState<SecurityAlertSummary | null>(null);

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [auditPagination, setAuditPagination] = useState<Pagination | null>(null);
  const [auditPage, setAuditPage] = useState(1);
  const [auditSearch, setAuditSearch] = useState('');
  const [auditSeverityFilter, setAuditSeverityFilter] = useState('');
  const [auditActionFilter, setAuditActionFilter] = useState('');
  const [availableActions, setAvailableActions] = useState<string[]>([]);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  // Security alerts state
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [alertsPagination, setAlertsPagination] = useState<Pagination | null>(null);
  const [alertsPage, setAlertsPage] = useState(1);
  const [alertStatusFilter, setAlertStatusFilter] = useState('');
  const [alertSeverityFilter, setAlertSeverityFilter] = useState('');

  // Anomaly state
  const [anomalySummary, setAnomalySummary] = useState<AnomalySummary | null>(null);

  // Logging config state
  const [loggingConfig, setLoggingConfig] = useState<LoggingConfig | null>(null);

  // ============================================================
  // Data fetching
  // ============================================================

  const fetchOverview = useCallback(async () => {
    setIsLoading(true);
    try {
      const [statsRes, summaryRes] = await Promise.all([
        adminApi.getAuditLogStats(timeRange),
        adminApi.getSecurityAlertSummary(timeRange),
      ]);
      setAuditStats(statsRes.stats);
      setAlertSummary(summaryRes.summary);
    } catch (error) {
      console.error('Failed to fetch overview:', error);
    } finally {
      setIsLoading(false);
    }
  }, [timeRange]);

  const fetchAuditLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const [logsRes, actionsRes] = await Promise.all([
        adminApi.getAuditLogs({
          page: auditPage,
          limit: 25,
          search: auditSearch || undefined,
          severity: auditSeverityFilter || undefined,
          action: auditActionFilter || undefined,
        }),
        adminApi.getAuditLogActions(),
      ]);
      setAuditLogs(logsRes.auditLogs);
      setAuditPagination(logsRes.pagination);
      setAvailableActions(actionsRes.actions);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setIsLoading(false);
    }
  }, [auditPage, auditSearch, auditSeverityFilter, auditActionFilter]);

  const fetchSecurityAlerts = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await adminApi.getSecurityAlerts({
        page: alertsPage,
        limit: 25,
        status: alertStatusFilter || undefined,
        severity: alertSeverityFilter || undefined,
      });
      setAlerts(res.alerts);
      setAlertsPagination(res.pagination);
    } catch (error) {
      console.error('Failed to fetch security alerts:', error);
    } finally {
      setIsLoading(false);
    }
  }, [alertsPage, alertStatusFilter, alertSeverityFilter]);

  const fetchAnomalies = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await adminApi.getAnomalySummary(timeRange);
      setAnomalySummary(res.summary);
    } catch (error) {
      console.error('Failed to fetch anomaly summary:', error);
    } finally {
      setIsLoading(false);
    }
  }, [timeRange]);

  const fetchLoggingConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await adminApi.getLoggingConfig();
      setLoggingConfig(res.config);
    } catch (error) {
      console.error('Failed to fetch logging config:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data based on active tab
  useEffect(() => {
    switch (activeTab) {
      case 'overview':
        fetchOverview();
        break;
      case 'audit-logs':
        fetchAuditLogs();
        break;
      case 'security-alerts':
        fetchSecurityAlerts();
        break;
      case 'anomalies':
        fetchAnomalies();
        break;
      case 'logging':
        fetchLoggingConfig();
        break;
    }
  }, [activeTab, fetchOverview, fetchAuditLogs, fetchSecurityAlerts, fetchAnomalies, fetchLoggingConfig]);

  const handleAlertStatusUpdate = async (alertId: string, newStatus: string) => {
    try {
      await adminApi.updateAlertStatus(alertId, newStatus);
      fetchSecurityAlerts();
    } catch (error) {
      console.error('Failed to update alert status:', error);
    }
  };

  const handleRefresh = () => {
    switch (activeTab) {
      case 'overview': fetchOverview(); break;
      case 'audit-logs': fetchAuditLogs(); break;
      case 'security-alerts': fetchSecurityAlerts(); break;
      case 'anomalies': fetchAnomalies(); break;
      case 'logging': fetchLoggingConfig(); break;
    }
  };

  // ============================================================
  // Render helpers
  // ============================================================

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatAction = (action: string) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const alertTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      FAILED_AUTH: 'Failed Authentication',
      BRUTE_FORCE: 'Brute Force Attack',
      PRIVILEGE_ESCALATION: 'Privilege Escalation',
      UNUSUAL_API_PATTERN: 'Unusual API Pattern',
      SUSPICIOUS_IP: 'Suspicious IP',
      ACCOUNT_TAKEOVER: 'Account Takeover',
      RATE_LIMIT_EXCEEDED: 'Rate Limit Exceeded',
      IMPOSSIBLE_TRAVEL: 'Impossible Travel',
    };
    return labels[type] || type;
  };

  // ============================================================
  // Render: Overview Tab
  // ============================================================

  const renderOverview = () => {
    if (isLoading && !auditStats) {
      return (
        <div className="space-y-6 animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-24 skeleton rounded-lg" />)}
          </div>
          <div className="h-64 skeleton rounded-lg" />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Activity}
            label={`Total Events (${timeRange}h)`}
            value={auditStats?.totalEvents || 0}
            color="blue"
          />
          <StatCard
            icon={AlertTriangle}
            label="Critical Events"
            value={auditStats?.criticalEvents || 0}
            color="red"
          />
          <StatCard
            icon={Shield}
            label="Open Security Alerts"
            value={alertSummary?.openAlerts || 0}
            color="yellow"
          />
          <StatCard
            icon={Lock}
            label="Critical Alerts"
            value={alertSummary?.criticalAlerts || 0}
            color="red"
          />
        </div>

        {/* Activity chart */}
        {auditStats?.hourlyBreakdown && auditStats.hourlyBreakdown.length > 0 && (
          <div className="card p-6">
            <h3 className="text-sm font-medium text-foreground mb-4">Audit Event Activity</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={auditStats.hourlyBreakdown}>
                  <XAxis
                    dataKey="hour"
                    stroke="#52525b"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => new Date(value).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                  />
                  <YAxis stroke="#52525b" fontSize={10} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#171717',
                      border: '1px solid #27272a',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                    labelStyle={{ color: '#a1a1aa' }}
                    labelFormatter={(value) => new Date(value).toLocaleString()}
                  />
                  <Bar dataKey="count" name="Events" fill="#6366f1" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Top actions and alerts by type */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top actions */}
          <div className="card p-6">
            <h3 className="text-sm font-medium text-foreground mb-4">Top Actions</h3>
            <div className="space-y-2">
              {auditStats?.actionBreakdown?.map((item) => (
                <div key={item.action} className="flex items-center justify-between py-1.5">
                  <span className="text-sm text-foreground-secondary font-mono">{item.action}</span>
                  <span className="text-sm font-medium text-foreground tabular-nums">{item.count.toLocaleString()}</span>
                </div>
              ))}
              {(!auditStats?.actionBreakdown || auditStats.actionBreakdown.length === 0) && (
                <p className="text-sm text-foreground-tertiary">No audit events in this time range</p>
              )}
            </div>
          </div>

          {/* Alerts by type */}
          <div className="card p-6">
            <h3 className="text-sm font-medium text-foreground mb-4">Alerts by Type</h3>
            <div className="space-y-2">
              {alertSummary?.alertsByType?.map((item) => (
                <div key={item.type} className="flex items-center justify-between py-1.5">
                  <span className="text-sm text-foreground-secondary">{alertTypeLabel(item.type)}</span>
                  <span className="text-sm font-medium text-foreground tabular-nums">{item.count}</span>
                </div>
              ))}
              {(!alertSummary?.alertsByType || alertSummary.alertsByType.length === 0) && (
                <p className="text-sm text-foreground-tertiary">No security alerts in this time range</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // Render: Audit Logs Tab
  // ============================================================

  const renderAuditLogs = () => {
    return (
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-muted" />
            <input
              type="text"
              placeholder="Search audit logs..."
              value={auditSearch}
              onChange={(e) => { setAuditSearch(e.target.value); setAuditPage(1); }}
              className="input pl-9"
            />
          </div>
          <div className="relative">
            <select
              value={auditSeverityFilter}
              onChange={(e) => { setAuditSeverityFilter(e.target.value); setAuditPage(1); }}
              className="input appearance-none pr-8 min-w-[140px]"
            >
              <option value="">All Severities</option>
              <option value="INFO">Info</option>
              <option value="WARNING">Warning</option>
              <option value="CRITICAL">Critical</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-muted pointer-events-none" />
          </div>
          <div className="relative">
            <select
              value={auditActionFilter}
              onChange={(e) => { setAuditActionFilter(e.target.value); setAuditPage(1); }}
              className="input appearance-none pr-8 min-w-[180px]"
            >
              <option value="">All Actions</option>
              {availableActions.map(a => (
                <option key={a} value={a}>{formatAction(a)}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-muted pointer-events-none" />
          </div>
        </div>

        {/* Table */}
        <div className="card overflow-hidden">
          {isLoading && !auditLogs.length ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Severity</th>
                    <th>Action</th>
                    <th>User ID</th>
                    <th>IP Address</th>
                    <th>Resource</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => (
                    <>
                      <tr key={log.id} className="cursor-pointer" onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}>
                        <td className="text-xs text-foreground-secondary whitespace-nowrap font-mono">{formatDate(log.createdAt)}</td>
                        <td><SeverityBadge severity={log.severity} /></td>
                        <td className="text-sm text-foreground font-mono">{log.action}</td>
                        <td className="text-xs text-foreground-tertiary font-mono">{log.userId ? `${log.userId.substring(0, 8)}...` : '—'}</td>
                        <td className="text-xs text-foreground-tertiary font-mono">{log.ipAddress || '—'}</td>
                        <td className="text-xs text-foreground-tertiary">{log.resource || '—'}</td>
                        <td>
                          <Eye className="w-4 h-4 text-foreground-muted hover:text-foreground transition-colors" />
                        </td>
                      </tr>
                      {expandedLogId === log.id && (
                        <tr key={`${log.id}-detail`}>
                          <td colSpan={7} className="!p-0">
                            <div className="bg-background-tertiary/50 p-4 border-t border-border/30">
                              <div className="grid grid-cols-2 gap-4 text-xs">
                                <div>
                                  <p className="text-foreground-tertiary mb-1">Full User ID</p>
                                  <p className="text-foreground font-mono">{log.userId || '—'}</p>
                                </div>
                                <div>
                                  <p className="text-foreground-tertiary mb-1">Resource ID</p>
                                  <p className="text-foreground font-mono">{log.resourceId || '—'}</p>
                                </div>
                                <div className="col-span-2">
                                  <p className="text-foreground-tertiary mb-1">User Agent</p>
                                  <p className="text-foreground font-mono text-[10px] break-all">{log.userAgent || '—'}</p>
                                </div>
                                {log.metadata && Object.keys(log.metadata).length > 0 && (
                                  <div className="col-span-2">
                                    <p className="text-foreground-tertiary mb-1">Metadata</p>
                                    <pre className="code-block text-[10px] !p-2 max-h-32 overflow-auto">
                                      {JSON.stringify(log.metadata, null, 2)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                  {auditLogs.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-foreground-tertiary">
                        No audit logs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {auditPagination && auditPagination.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-foreground-tertiary">
              Showing {((auditPagination.page - 1) * auditPagination.limit) + 1}–{Math.min(auditPagination.page * auditPagination.limit, auditPagination.total)} of {auditPagination.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAuditPage(p => Math.max(1, p - 1))}
                disabled={auditPagination.page <= 1}
                className="btn-ghost p-1.5 disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-foreground-secondary tabular-nums">
                Page {auditPagination.page} of {auditPagination.totalPages}
              </span>
              <button
                onClick={() => setAuditPage(p => Math.min(auditPagination!.totalPages, p + 1))}
                disabled={auditPagination.page >= auditPagination.totalPages}
                className="btn-ghost p-1.5 disabled:opacity-30"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================================
  // Render: Security Alerts Tab
  // ============================================================

  const renderSecurityAlerts = () => {
    return (
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <select
              value={alertStatusFilter}
              onChange={(e) => { setAlertStatusFilter(e.target.value); setAlertsPage(1); }}
              className="input appearance-none pr-8 min-w-[160px]"
            >
              <option value="">All Statuses</option>
              <option value="OPEN">Open</option>
              <option value="ACKNOWLEDGED">Acknowledged</option>
              <option value="INVESTIGATING">Investigating</option>
              <option value="RESOLVED">Resolved</option>
              <option value="FALSE_POSITIVE">False Positive</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-muted pointer-events-none" />
          </div>
          <div className="relative">
            <select
              value={alertSeverityFilter}
              onChange={(e) => { setAlertSeverityFilter(e.target.value); setAlertsPage(1); }}
              className="input appearance-none pr-8 min-w-[140px]"
            >
              <option value="">All Severities</option>
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
              <option value="CRITICAL">Critical</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-muted pointer-events-none" />
          </div>
        </div>

        {/* Alerts list */}
        <div className="space-y-3">
          {isLoading && !alerts.length ? (
            <div className="card p-8 text-center">
              <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : alerts.length === 0 ? (
            <div className="card p-8 text-center">
              <Shield className="w-10 h-10 text-green-400 mx-auto mb-3" />
              <p className="text-foreground-secondary">No security alerts found</p>
              <p className="text-xs text-foreground-tertiary mt-1">Your system is looking good</p>
            </div>
          ) : (
            alerts.map((alert) => (
              <div key={alert.id} className="card p-4">
                <div className="flex items-start gap-4">
                  <div className={clsx(
                    'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5',
                    alert.severity === 'CRITICAL' ? 'bg-red-500/10 text-red-400' :
                    alert.severity === 'HIGH' ? 'bg-orange-500/10 text-orange-400' :
                    alert.severity === 'MEDIUM' ? 'bg-yellow-500/10 text-yellow-400' :
                    'bg-blue-500/10 text-blue-400'
                  )}>
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="text-sm font-medium text-foreground">{alert.title}</h4>
                      <SeverityBadge severity={alert.severity} />
                      <StatusBadge status={alert.status} />
                    </div>
                    <p className="text-xs text-foreground-secondary mb-2">{alert.description}</p>
                    <div className="flex items-center gap-4 text-xs text-foreground-tertiary">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(alert.createdAt)}
                      </span>
                      <span className="font-mono">{alertTypeLabel(alert.type)}</span>
                      {alert.ipAddress && (
                        <span className="flex items-center gap-1 font-mono">
                          <Globe className="w-3 h-3" />
                          {alert.ipAddress}
                        </span>
                      )}
                    </div>
                    {alert.metadata && Object.keys(alert.metadata).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs text-foreground-tertiary cursor-pointer hover:text-foreground-secondary">
                          View Details
                        </summary>
                        <pre className="code-block text-[10px] !p-2 mt-1 max-h-24 overflow-auto">
                          {JSON.stringify(alert.metadata, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                  {/* Actions */}
                  {alert.status !== 'RESOLVED' && alert.status !== 'FALSE_POSITIVE' && (
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {alert.status === 'OPEN' && (
                        <button
                          onClick={() => handleAlertStatusUpdate(alert.id, 'ACKNOWLEDGED')}
                          className="btn-ghost text-xs py-1 px-2"
                          title="Acknowledge"
                        >
                          <Eye className="w-3 h-3" />
                        </button>
                      )}
                      {(alert.status === 'OPEN' || alert.status === 'ACKNOWLEDGED') && (
                        <button
                          onClick={() => handleAlertStatusUpdate(alert.id, 'INVESTIGATING')}
                          className="btn-ghost text-xs py-1 px-2"
                          title="Investigate"
                        >
                          <Filter className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        onClick={() => handleAlertStatusUpdate(alert.id, 'RESOLVED')}
                        className="btn-ghost text-xs py-1 px-2 text-green-400"
                        title="Resolve"
                      >
                        <CheckCircle2 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleAlertStatusUpdate(alert.id, 'FALSE_POSITIVE')}
                        className="btn-ghost text-xs py-1 px-2 text-zinc-400"
                        title="False Positive"
                      >
                        <XCircle className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {alertsPagination && alertsPagination.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-foreground-tertiary">
              Showing {((alertsPagination.page - 1) * alertsPagination.limit) + 1}–{Math.min(alertsPagination.page * alertsPagination.limit, alertsPagination.total)} of {alertsPagination.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAlertsPage(p => Math.max(1, p - 1))}
                disabled={alertsPagination.page <= 1}
                className="btn-ghost p-1.5 disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-foreground-secondary tabular-nums">
                Page {alertsPagination.page} of {alertsPagination.totalPages}
              </span>
              <button
                onClick={() => setAlertsPage(p => Math.min(alertsPagination!.totalPages, p + 1))}
                disabled={alertsPagination.page >= alertsPagination.totalPages}
                className="btn-ghost p-1.5 disabled:opacity-30"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================================
  // Render: Anomaly Detection Tab
  // ============================================================

  const renderAnomalies = () => {
    if (isLoading && !anomalySummary) {
      return (
        <div className="space-y-6 animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => <div key={i} className="h-24 skeleton rounded-lg" />)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => <div key={i} className="h-48 skeleton rounded-lg" />)}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            icon={Lock}
            label={`Failed Logins (${timeRange}h)`}
            value={anomalySummary?.failedLogins.total || 0}
            color={anomalySummary && anomalySummary.failedLogins.total > 0 ? 'red' : 'green'}
          />
          <StatCard
            icon={Activity}
            label="API Anomalies"
            value={anomalySummary?.apiAnomalies.total || 0}
            color={anomalySummary && anomalySummary.apiAnomalies.total > 0 ? 'yellow' : 'green'}
          />
          <StatCard
            icon={Shield}
            label="Privilege Escalation Attempts"
            value={anomalySummary?.privilegeEscalations.total || 0}
            color={anomalySummary && anomalySummary.privilegeEscalations.total > 0 ? 'red' : 'green'}
          />
        </div>

        {/* Failed login details */}
        {anomalySummary && anomalySummary.failedLogins.total > 0 && (
          <div className="card p-6">
            <h3 className="text-sm font-medium text-foreground mb-1">Failed Login Analysis</h3>
            <p className="text-xs text-foreground-tertiary mb-4">
              {anomalySummary.failedLogins.total} failed attempts from {anomalySummary.failedLogins.uniqueIPs} unique IPs targeting {anomalySummary.failedLogins.uniqueEmails} accounts
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top offending IPs */}
              <div>
                <h4 className="text-xs font-medium text-foreground-secondary mb-2">Top Offending IPs</h4>
                <div className="space-y-1.5">
                  {anomalySummary.topOffendingIPs.map((item) => (
                    <div key={item.ip} className="flex items-center justify-between py-1 px-2 bg-background-tertiary/50 rounded">
                      <span className="text-xs text-foreground font-mono">{item.ip}</span>
                      <span className={clsx(
                        'text-xs font-medium tabular-nums',
                        item.count >= 10 ? 'text-red-400' : item.count >= 5 ? 'text-yellow-400' : 'text-foreground-secondary'
                      )}>
                        {item.count} attempts
                      </span>
                    </div>
                  ))}
                  {anomalySummary.topOffendingIPs.length === 0 && (
                    <p className="text-xs text-foreground-tertiary">No data</p>
                  )}
                </div>
              </div>

              {/* Top targeted accounts */}
              <div>
                <h4 className="text-xs font-medium text-foreground-secondary mb-2">Top Targeted Accounts</h4>
                <div className="space-y-1.5">
                  {anomalySummary.topTargetedAccounts.map((item) => (
                    <div key={item.email} className="flex items-center justify-between py-1 px-2 bg-background-tertiary/50 rounded">
                      <span className="text-xs text-foreground font-mono truncate mr-2">{item.email}</span>
                      <span className={clsx(
                        'text-xs font-medium tabular-nums flex-shrink-0',
                        item.count >= 10 ? 'text-red-400' : item.count >= 5 ? 'text-yellow-400' : 'text-foreground-secondary'
                      )}>
                        {item.count} attempts
                      </span>
                    </div>
                  ))}
                  {anomalySummary.topTargetedAccounts.length === 0 && (
                    <p className="text-xs text-foreground-tertiary">No data</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Clean state */}
        {anomalySummary && anomalySummary.failedLogins.total === 0 && anomalySummary.apiAnomalies.total === 0 && anomalySummary.privilegeEscalations.total === 0 && (
          <div className="card p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-foreground mb-1">All Clear</h3>
            <p className="text-sm text-foreground-tertiary">
              No anomalies detected in the last {timeRange} hours. Your system is secure.
            </p>
          </div>
        )}
      </div>
    );
  };

  // ============================================================
  // Render: Centralized Logging Tab
  // ============================================================

  const renderLogging = () => {
    if (isLoading && !loggingConfig) {
      return (
        <div className="space-y-4 animate-pulse">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 skeleton rounded-lg" />)}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-medium text-foreground mb-1">Centralized Logging Integrations</h3>
          <p className="text-xs text-foreground-tertiary mb-4">
            Configure external logging services to forward audit events for centralized monitoring and alerting.
            Set environment variables to enable each integration.
          </p>
        </div>

        <div className="space-y-3">
          <LoggingStatusCard
            name="Amazon CloudWatch"
            icon={Server}
            enabled={loggingConfig?.cloudwatch.enabled || false}
            details={loggingConfig?.cloudwatch.enabled
              ? `Log Group: ${loggingConfig.cloudwatch.logGroup} | Region: ${loggingConfig.cloudwatch.region}`
              : 'Set CLOUDWATCH_AUDIT_ENABLED=true to enable'
            }
          />
          <LoggingStatusCard
            name="Datadog"
            icon={Activity}
            enabled={loggingConfig?.datadog.enabled || false}
            details={loggingConfig?.datadog.enabled
              ? `Site: ${loggingConfig.datadog.site}`
              : 'Set DATADOG_AUDIT_ENABLED=true and DATADOG_API_KEY to enable'
            }
          />
          <LoggingStatusCard
            name="Elasticsearch (ELK Stack)"
            icon={Search}
            enabled={loggingConfig?.elk.enabled || false}
            details={loggingConfig?.elk.enabled
              ? `URL: ${loggingConfig.elk.url} | Index: ${loggingConfig.elk.indexPrefix}-*`
              : 'Set ELK_AUDIT_ENABLED=true and ELASTICSEARCH_URL to enable'
            }
          />
          <LoggingStatusCard
            name="Webhook (Custom)"
            icon={Globe}
            enabled={loggingConfig?.webhook.enabled || false}
            details={loggingConfig?.webhook.enabled
              ? `Endpoint: ${loggingConfig.webhook.url}`
              : 'Set AUDIT_WEBHOOK_ENABLED=true and AUDIT_WEBHOOK_URL to enable'
            }
          />
        </div>

        {/* Configuration guide */}
        <div className="card p-6">
          <h3 className="text-sm font-medium text-foreground mb-3">Configuration Guide</h3>
          <div className="space-y-4">
            <div>
              <h4 className="text-xs font-medium text-foreground-secondary mb-1">CloudWatch</h4>
              <pre className="code-block text-[11px] !p-3">{`CLOUDWATCH_AUDIT_ENABLED=true
CLOUDWATCH_AUDIT_LOG_GROUP=/gpu-cloud/audit-logs
CLOUDWATCH_AUDIT_LOG_STREAM=client-dashboard
AWS_REGION=us-east-1`}</pre>
            </div>
            <div>
              <h4 className="text-xs font-medium text-foreground-secondary mb-1">Datadog</h4>
              <pre className="code-block text-[11px] !p-3">{`DATADOG_AUDIT_ENABLED=true
DATADOG_API_KEY=your-api-key
DATADOG_SITE=datadoghq.com`}</pre>
            </div>
            <div>
              <h4 className="text-xs font-medium text-foreground-secondary mb-1">Elasticsearch (ELK)</h4>
              <pre className="code-block text-[11px] !p-3">{`ELK_AUDIT_ENABLED=true
ELASTICSEARCH_URL=http://elasticsearch:9200
ELK_INDEX_PREFIX=gpu-cloud-audit`}</pre>
            </div>
            <div>
              <h4 className="text-xs font-medium text-foreground-secondary mb-1">Custom Webhook</h4>
              <pre className="code-block text-[11px] !p-3">{`AUDIT_WEBHOOK_ENABLED=true
AUDIT_WEBHOOK_URL=https://your-endpoint.com/audit
AUDIT_WEBHOOK_HEADERS={"Authorization":"Bearer token"}`}</pre>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // Main Render
  // ============================================================

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Audit & Security</h1>
          <p className="text-foreground-secondary mt-1 text-sm">
            Monitor audit events, security alerts, and anomaly detection
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Time range selector */}
          <div className="relative">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(parseInt(e.target.value, 10))}
              className="input appearance-none pr-8 min-w-[120px]"
            >
              <option value={1}>Last 1h</option>
              <option value={6}>Last 6h</option>
              <option value={24}>Last 24h</option>
              <option value={72}>Last 3 days</option>
              <option value={168}>Last 7 days</option>
              <option value={720}>Last 30 days</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-muted pointer-events-none" />
          </div>
          <button onClick={handleRefresh} className="btn-secondary" disabled={isLoading}>
            <RefreshCw className={clsx('w-4 h-4', isLoading && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap -mb-px',
              activeTab === tab.id
                ? 'border-accent text-accent'
                : 'border-transparent text-foreground-secondary hover:text-foreground hover:border-border'
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'audit-logs' && renderAuditLogs()}
      {activeTab === 'security-alerts' && renderSecurityAlerts()}
      {activeTab === 'anomalies' && renderAnomalies()}
      {activeTab === 'logging' && renderLogging()}
    </div>
  );
}
